import * as React from "react";
import {useEffect} from "react";
import {Button, Header, Label, Loader, Modal} from "semantic-ui-react";
import AceEditor from "react-ace";
import {fetchResponsibleManagers} from "./CustomerReportFilter";
import {customerReport} from "../../../../style/page_with_sidebar";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {
    AddAcknowledgementForCompaniesCustomerReportRequest,
    AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest
} from "../../../../../generated/api";
import {authentication} from "../../../../../authentication";
import {backend} from "../../../../../xconvert-backend";

import {
    EnrichedCompanyApiStatus,
    EnrichedCompanyMessageLogReport,
    EnrichedErrorsFilesCountCompany, EnrichedFtpFoldersFileHealth,
    EnrichedNeedsVerificationFilesCountCompany,
    EnrichedQueuedEventsHealthCompanyReport,
    EnrichedSchedulerReportTaskCompanyStatus,
    EnrichedSftpSyncResult,
    EnrichedStatusJsonReportCompany, EnrichedWorkerHealth,
    ServiceMonitoringRecord
} from "../../../../../generated-internal-monitoring/api";
import ServiceTypeEnum = AddAcknowledgementForCompaniesCustomerReportRequest.ServiceTypeEnum;

export interface CustomerReportTextsForPMsModalProps {
    isOpen: boolean
    onClose: () => void
}

export function CustomerReportTextsForPMsModal(props: React.PropsWithChildren<CustomerReportTextsForPMsModalProps>) {

    const [selectedManager, setSelectedManager] = React.useState<string | undefined>(undefined)
    const [textToSend, setTextToSend] = React.useState<string>("")
    const [brokenCompaniesByManager, setBrokenCompaniesByManager] = React.useState<{
        [manager: string]: BrokenCompany[]
    }>({})
    const [loading, setLoading] = React.useState<boolean>(false)

    useEffect(() => {
        if (!props.isOpen) return;
        setLoading(true);
        fetchResponsibleManagers().then(r => {
            console.debug("Responsible managers fetched: ", r);
            getAllBrokenCompaniesGroupedByManager(r)
            setLoading(false);
        })
    }, [props.isOpen]);

    useEffect(() => {
        createTextToSend(selectedManager)
    }, [selectedManager])


    interface BrokenCompany {
        companyId: string
        name: string
        issues: Issue[]
    }

    interface Issue {
        message: string,
        service: AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum,
        disableUntilObject: any
    }

    function getBrokenCompaniesForManager(manager: string | undefined) {
        let brokenCompanies: BrokenCompany[] = []

        function addIssue(
            companyId: string,
            name: string,
            issue: Issue
        ) {
            if (brokenCompanies.find(c => c.companyId === companyId) !== undefined) {
                brokenCompanies.find(c => c.companyId === companyId)?.issues.push(issue)
            } else {
                brokenCompanies.push({
                    companyId: companyId,
                    name: name,
                    issues: [issue]
                })
            }
        }

        function filterIssues(companies: any[]) {
            return companies?.filter(
                c => c.responsibleManager === manager &&
                    isIgnored(c.ignoreStatus) === false
            )
        }

        function isIgnored(ignoreStatus: ServiceMonitoringRecord): boolean {

            if (!ignoreStatus) return false;

            return ignoreStatus.state == ServiceMonitoringRecord.StateEnum.IGNORE &&
                ignoreStatus.enabled === true;

        }

        let serviceTypes = Object.values(AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum)
        serviceTypes.forEach((serviceType) => {
            switch (serviceType) {
                case ServiceTypeEnum.MESSAGELOGREPORT:
                    addMessageLogHealthIssues();
                    break;
                case ServiceTypeEnum.APIGATEWAYREPORT:
                    addApiGatewayStatusIssues();
                    break;
                case ServiceTypeEnum.ERRORFILESREPORT:
                    addErrorFilesCountIssues();
                    break;
                case ServiceTypeEnum.STATUSJSONREPORT:
                    addStatusJsonReportIssues();
                    break;
                case ServiceTypeEnum.NEEDSVERIFICATIONREPORT:
                    addNeedsVerificationFilesCountIssues();
                    break;
                case ServiceTypeEnum.SCHEDULERREPORTTASKSTATUS:
                    addSchedulerReportTaskStatusIssues();
                    break;
                case ServiceTypeEnum.QUEUEREPORT:
                    addQueueStatusIssues();
                    break;
                case ServiceTypeEnum.SFTPREPORT:
                    addSftpReportIssues();
                    break;
                case ServiceTypeEnum.CUSTOMERFILEHEALTHREPORT:
                    addCustomerFileHealthReportIssues();
                    break;
                case ServiceTypeEnum.LOGENIOSFILEHEALTHREPORT:
                    addLogeniosFileHealthReportIssues();
                    break;
                case ServiceTypeEnum.ATTACHMENTFILEHEALTHREPORT:
                    addAttachmentFileHealthReportIssues();
                    break;
                case ServiceTypeEnum.ETACALCULATORREPORT:
                    addEtaCalculatorReportIssues();
                    break;
                default:
                    break;
            }
        })

        function addMessageLogHealthIssues() {
            filterIssues(customerReport.value?.messageLogHealth?.companies)
                ?.filter((c: EnrichedCompanyMessageLogReport) => c.sendErrors > 0)
                .forEach((company: EnrichedCompanyMessageLogReport) => {
                    let issue = {
                        service: ServiceTypeEnum.MESSAGELOGREPORT,
                        message: "hat " + company.sendErrors + " Fehler in den MessageLogs\n"
                    } as Issue
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addApiGatewayStatusIssues() {
            filterIssues(customerReport.value?.apiGatewayStatus?.companyApiStatus)
                ?.filter((c: EnrichedCompanyApiStatus) => c.status !== "OK")
                .forEach((company: EnrichedCompanyApiStatus) => {
                    let issue = {
                        service: ServiceTypeEnum.APIGATEWAYREPORT,
                        message: "hat einen API Gateway Status ungleich OK (" + company.status + ")\n"
                    } as Issue
                    addIssue(company.companyId, company.companyName, issue)
                })
        }

        function addErrorFilesCountIssues() {
            filterIssues(customerReport.value?.errorFilesCount?.companies)
                ?.filter((c: EnrichedErrorsFilesCountCompany) => c.filesOlderThanFiveMinutes > 0)
                .forEach((company: EnrichedErrorsFilesCountCompany) => {
                    let issue: Issue = {
                        service: ServiceTypeEnum.ERRORFILESREPORT,
                        message: "hat " + company.filesOlderThanFiveMinutes + " Error Files\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addStatusJsonReportIssues() {
            filterIssues(customerReport.value?.statusJsonReport?.companies)
                ?.filter((c: EnrichedStatusJsonReportCompany) => c.healthy != true)
                .forEach((company: EnrichedStatusJsonReportCompany) => {
                    let issue: Issue = {
                        service: ServiceTypeEnum.STATUSJSONREPORT,
                        message: "hat Probleme im status.json." + (company.statusLine ? " StatusLine: " + company.statusLine : "") + "\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addNeedsVerificationFilesCountIssues() {
            filterIssues(customerReport.value?.needsVerificationFilesCount?.companies)
                ?.filter((c: EnrichedNeedsVerificationFilesCountCompany) => c.filesOlderThanFiveMinutes > 0)
                .forEach((company: EnrichedNeedsVerificationFilesCountCompany) => {
                    let issue: Issue = {
                        service: ServiceTypeEnum.NEEDSVERIFICATIONREPORT,
                        message: "hat " + company.filesOlderThanFiveMinutes + " Dateien, die eine manuelle Prüfung benötigen\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addSchedulerReportTaskStatusIssues() {
            filterIssues(customerReport.value?.schedulerReportTaskStatus?.companies)
                ?.filter((c: EnrichedSchedulerReportTaskCompanyStatus) => c.unhealthyTasks > 0)
                .forEach((company: EnrichedSchedulerReportTaskCompanyStatus) => {
                    let issue: Issue = {
                        service: ServiceTypeEnum.SCHEDULERREPORTTASKSTATUS,
                        message: "hat " + company.unhealthyTasks + " Scheduler Report Tasks\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.companyName, issue)
                })
        }

        function addQueueStatusIssues() {
            filterIssues(customerReport.value?.queueStatus?.companies)
                ?.filter((c: EnrichedQueuedEventsHealthCompanyReport) => c.unprocessedEventsOlderThanFiveMinutes > 0)
                .forEach((company: EnrichedQueuedEventsHealthCompanyReport) => {
                    let issue: Issue = {
                        service: ServiceTypeEnum.QUEUEREPORT,
                        message: "hat " + company.unprocessedEventsOlderThanFiveMinutes + " QueuedEvents, die länger als 5 Minuten unprocessed sind\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addSftpReportIssues() {
            filterIssues(customerReport.value?.fileSystemWorkerStatus?.sftpSyncResults)
                ?.filter((c: EnrichedSftpSyncResult) => c.status !== 'SUCCESS')
                .forEach((company: EnrichedSftpSyncResult) => {
                    let issueEntry: Issue = {
                        service: ServiceTypeEnum.SFTPREPORT,
                        message: " hat sync '" + company.name + "' mit status: '" + company.status + "'\n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.name, issueEntry)
                })
        }

        function addCustomerFileHealthReportIssues() {
            filterIssues(customerReport.value?.fileSystemWorkerStatus?.customerOldFiles)
                ?.filter((c: EnrichedFtpFoldersFileHealth) => c.healthy !== true)
                .forEach((company: EnrichedFtpFoldersFileHealth) => {

                    let issueEntry: Issue = {
                        service: ServiceTypeEnum.CUSTOMERFILEHEALTHREPORT,
                        message: "hat " + company.oldFileAmount + " alte Dateien in logenios2customer \n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.companyName, issueEntry)

                })
        }

        function addLogeniosFileHealthReportIssues() {
            filterIssues(customerReport.value?.fileSystemWorkerStatus?.logeniosOldFiles)
                ?.filter((c: EnrichedFtpFoldersFileHealth) => c.healthy !== true)
                .forEach((company: EnrichedFtpFoldersFileHealth) => {

                    let issueEntry: Issue = {
                        service: ServiceTypeEnum.LOGENIOSFILEHEALTHREPORT,
                        message: "hat " + company.oldFileAmount + " alte Dateien in customer2logenios \n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.companyName, issueEntry)

                })
        }

        function addEtaCalculatorReportIssues() {
            filterIssues(customerReport.value?.etaCalculatorHealth?.workerThreads)
                ?.filter((c: EnrichedWorkerHealth) => c.isHealthy !== true)
                .forEach((company: EnrichedWorkerHealth) => {
                    let issue = {
                        service: ServiceTypeEnum.ETACALCULATORREPORT,
                        message: "ETA-Calculator ist nicht healthy \n",
                        disableUntilObject: company
                    } as Issue
                    addIssue(company.companyId, company.name, issue)
                })
        }

        function addAttachmentFileHealthReportIssues() {
            filterIssues(customerReport.value?.fileSystemWorkerStatus?.attachmentOldFiles)
                ?.filter((c: EnrichedFtpFoldersFileHealth) => c.healthy !== true)
                .forEach((company: EnrichedFtpFoldersFileHealth) => {

                    let issueEntry: Issue = {
                        service: ServiceTypeEnum.ATTACHMENTFILEHEALTHREPORT,
                        message: "hat " + company.oldFileAmount + " alte Dateien in attachments \n",
                        disableUntilObject: company
                    }
                    addIssue(company.companyId, company.companyName, issueEntry)

                })
        }

        return brokenCompanies
    }

    function getAllBrokenCompaniesGroupedByManager(managers?: string[]) {
        let brokenCompaniesByManager: { [manager: string]: any[] } = {}

        managers.forEach((manager) => {
            let brokenCompanies = getBrokenCompaniesForManager(manager)
            if (brokenCompanies.length > 0) {
                brokenCompaniesByManager[manager] = brokenCompanies
                console.debug("[getAllBrokenCompaniesGroupedByManager] found broken companies for manager", manager, brokenCompanies)
            }
        })
        console.debug("[getAllBrokenCompaniesGroupedByManager] brokenCompaniesByManager", brokenCompaniesByManager)
        setBrokenCompaniesByManager(brokenCompaniesByManager)
    }

    function createTextToSend(manager: string | undefined) {
        setTextToSend("Hey, hier der aktuelle Stand deiner Firmen:\n\n")

        getBrokenCompaniesForManager(manager).forEach((company) => {
            setTextToSend((prev) => prev + "- " + decrypt(company.name) + " (" + company.companyId + "):\n"
                + company.issues.map(i => "   - " + i.message).join("") + "\n")
        })
    }

    function decrypt(text: string): string {
        return decodeURIComponent(text);
    }

    function handleClose() {
        props.onClose()
    }

    function displayManagerOverview() {
        console.debug("[displayManagerOverview] brokenCompaniesByManager", brokenCompaniesByManager)
        return <div key={brokenCompaniesByManager.toString()}>
            {Object.keys(brokenCompaniesByManager).map((manager) => {
                let companies = brokenCompaniesByManager[manager]
                let totalIssues = companies.reduce((acc, company) => acc + company.issues.length, 0)
                return <Label
                    key={manager}
                    color={manager === selectedManager ? 'blue' : 'grey'}
                    onClick={() => setSelectedManager(manager)}
                >
                    {manager}: {companies.length} Firmen, {totalIssues} Probleme
                </Label>
            })}
        </div>
    }

    async function ignore(
        currentService: AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum,
        disableUntilObjects: any[],
        companyIds: string[],
    ) {
        const payload: AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest = {
            companyIds: companyIds,        // IDs of the selected companies
            disableOptions: AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.DisableOptionsEnum.UNTILCHANGE,
            serviceType: currentService,    // Current service type (e.g., "Sftp", "ApiGateway")
            enable: false,
            disableUntilObject: disableUntilObjects, // Add selected company objects when UNTILCHANGE is selected
            personWhoIgnored: authentication.user.email ?? "",
            reasonToIgnore: ""
        };
        console.debug('Payload to be sent to backend to ignore:', payload);
        try {
            console.debug('Sending to backend:', payload);
            let auth = backend.withTokenAuthHeader(authentication.token);
            const result = await backend.companyApi.addOrRemoveCompaniesFromIgnoreListForCustomerReport(payload, auth);

            if (result) {
                console.debug('Successfully updated companies:', result);

            } else {
                console.error('Failed to update companies:', result);
            }

        } catch (error) {
            console.error('Error while sending request:', error);
        }
    }

    function ignoreByType(groupedByService: {
        API_GATEWAY_REPORT?: any[];
        SFTP_REPORT?: any[];
        CUSTOMER_FILE_HEALTH_REPORT?: any[];
        LOGENIOS_FILE_HEALTH_REPORT?: any[];
        ATTACHMENT_FILE_HEALTH_REPORT?: any[];
        QUEUE_REPORT?: any[];
        STATUS_JSON_REPORT?: any[];
        NEEDS_VERIFICATION_REPORT?: any[];
        ERROR_FILES_REPORT?: any[];
        ETA_CALCULATOR_REPORT?: any[];
        MESSAGE_LOG_REPORT?: any[];
        SCHEDULER_REPORT_TASK_STATUS?: any[]
    }) {

        Object.keys(groupedByService).forEach((service) => {
            let serviceEnum = service as AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum
            let companies = groupedByService[serviceEnum] ?? []
            ignore(
                serviceEnum,
                companies.map(c => c.issues.map(i => i.disableUntilObject)).flat(),
                companies.map(c => c.companyId)
            ).then()
        })
    }

    function displayIgnorePanel() {

        let currentServices = brokenCompaniesByManager[selectedManager]

        let groupedByService: { [service in AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum]?: any[] } = {}

        currentServices?.forEach((company) => {
            company.issues.forEach((issue: Issue) => {
                if (!groupedByService[issue.service]) {
                    groupedByService[issue.service] = []
                }
                groupedByService[issue.service]?.push(company)
            })
        })


        return <div>
            <h3>Ignore all for {selectedManager}</h3>

            <Button
                primary
                onClick={() => ignoreByType(groupedByService)}
                disabled={!selectedManager}
            >
                Ignore ALL companies/issues for {selectedManager}
            </Button>
            <hr/>
            {Object.keys(groupedByService).map((service) => {
                let serviceEnum = service as AddOrRemoveCompaniesFromIgnoreListForCustomerReportRequest.ServiceTypeEnum
                let companies = groupedByService[serviceEnum] ?? []
                return <div key={service}>
                    <h4>{service} <span
                        style={{fontWeight: "normal", fontSize: "0.9em"}}>({companies.length} Firmen)</span></h4>
                    <ul style={{marginTop: 0, marginBottom: 8, fontSize: "0.95em"}}>
                        {companies.map((c: any) => (
                            <li key={c.companyId} style={{fontSize: "0.95em"}}>
                                {decrypt(c.name)} ({c.companyId})
                            </li>
                        ))}
                    </ul>

                    <Button
                        onClick={() => ignore(
                            serviceEnum,
                            companies.map(c => c.issues.map(i => i.disableUntilObject)).flat(),
                            companies.map(c => c.companyId)
                        )}
                        disabled={!selectedManager}
                    >
                        Ignore {service} issues for {selectedManager}
                    </Button>
                </div>
            })}

        </div>


    }

    return <Modal
        open={props.isOpen}
        onClose={handleClose}
        dimmer="inverted"
        size='fullscreen'>
        <Header icon='browser' content='select a responsible manager and send him the text'/>
        <Modal.Content>
            {displayManagerOverview()}
            {loading && <Loader active inline='centered' content='Loading... Please wait.'/>}
            {!loading && <Splitter
                name={"CustomerReportTextsForPmModalSplitter"}
                left={<AceEditor
                    theme="monokai"
                    mode='groovy'
                    value={textToSend}
                    onChange={(v) => setTextToSend(v)}
                    placeholder='textToSend'
                    width="100%"
                    height="800px"
                    readOnly={false}
                />}
                right={displayIgnorePanel()}
            />}

            <Button onClick={handleClose}>Cancel</Button>
        </Modal.Content>
    </Modal>
}